/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { InventoryIsoDateField } from '../../../shared/ui/InventoryIsoDateField';
import { MasterFilterCompactPicker } from '../../../shared/filters/MasterFilterCompactPicker'
import { MasterFilterSheetModal } from '../../../shared/filters/MasterFilterSheetModal'
import { OperationalFilterChipScrollRow, OperationalFilterFieldLabel, OperationalFilterPickerRow, OperationalFilterSectionDivider } from '../../../shared/filters/OperationalFilterPrimitives'
import { OperationalFiltersCard } from '../../../shared/filters/OperationalFiltersCard'
import { OperationalFiltersClearRow } from '../../../shared/filters/OperationalFiltersClearRow'
import { operationalFilterStyles as opStyles } from '../../../shared/filters/operationalFilters.styles'
import { useOperationalFilterLayout } from '../../../shared/filters/useOperationalFilterLayout';
import {
  AUDIT_PERIOD_PRESETS,
  type AuditPeriodPreset,
  type TeamHistoryFilters,
} from './useTeamHistoryFilters';

const AUDIT_FILTERS_COLLAPSED_STORAGE_KEY = 'audit_filters_collapsed';

type UserOption = { id: string; name: string };

type Props = {
  filters: TeamHistoryFilters;
  userOptions: UserOption[];
  showUserFilter?: boolean;
};

export function TeamHistoryFiltersPanel({ filters, userOptions, showUserFilter = true }: Props) {
  const { variant } = useOperationalFilterLayout();
  const isMobile = variant === 'mobile';

  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let alive = true;
    void AsyncStorage.getItem(AUDIT_FILTERS_COLLAPSED_STORAGE_KEY).then((raw) => {
      if (!alive) return;
      setCollapsed(raw === '1');
    });
    return () => { alive = false; };
  }, []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    void AsyncStorage.setItem(AUDIT_FILTERS_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
  };

  const [periodSheetOpen, setPeriodSheetOpen] = useState(false);
  const [userSheetOpen, setUserSheetOpen] = useState(false);

  const periodPickerLabel = useMemo(
    () => AUDIT_PERIOD_PRESETS.find((p) => p.id === filters.periodPreset)?.label ?? 'Hoy',
    [filters.periodPreset],
  );

  const userPickerLabel = useMemo(() => {
    if (!filters.userFilter) return 'Todos';
    return userOptions.find((u) => u.id === filters.userFilter)?.name ?? 'Usuario';
  }, [filters.userFilter, userOptions]);

  const periodSheetOptions = useMemo(
    () => AUDIT_PERIOD_PRESETS.map((p) => ({ id: p.id, name: p.label })),
    [],
  );

  const customDateRow =
    filters.periodPreset === 'custom' ? (
      <View style={styles.customDateRow}>
        <View style={styles.customDateField}>
          <InventoryIsoDateField
            value={filters.customFromDate}
            onChange={filters.setCustomFromDate}
            emptyLabel="Desde"
            ariaLabel="Fecha desde"
            showClearButton={false}
          />
        </View>
        <View style={styles.customDateField}>
          <InventoryIsoDateField
            value={filters.customToDate}
            onChange={filters.setCustomToDate}
            emptyLabel="Hasta"
            ariaLabel="Fecha hasta"
            showClearButton={false}
          />
        </View>
      </View>
    ) : null;

  const metaRow = filters.hasActiveFilters ? (
    <OperationalFiltersClearRow hasActiveFilters onClearFilters={filters.clearFilters} />
  ) : null;

  const desktopCompactPickers = (
    <>
      <View style={opStyles.wideToolbarRow}>
        <View style={opStyles.wideToolbarGroup}>
          <MasterFilterCompactPicker
            caption="Período"
            value={periodPickerLabel}
            active={filters.periodPreset !== 'today'}
            dense
            onPress={() => setPeriodSheetOpen(true)}
          />
        </View>
        {showUserFilter ? (
          <View style={opStyles.wideToolbarGroup}>
            <MasterFilterCompactPicker
              caption="Usuario"
              value={userPickerLabel}
              active={filters.userFilter != null}
              dense
              onPress={() => setUserSheetOpen(true)}
            />
          </View>
        ) : null}
      </View>
      {customDateRow}
    </>
  );

  const mobileFilters = (
    <>
      <OperationalFilterFieldLabel>Período</OperationalFilterFieldLabel>
      <OperationalFilterChipScrollRow
        options={AUDIT_PERIOD_PRESETS.map((p) => ({ value: p.id, label: p.label }))}
        value={filters.periodPreset}
        onChange={filters.setPeriodPreset}
      />
      {customDateRow}

      {showUserFilter ? (
        <>
          <OperationalFilterSectionDivider />
          <OperationalFilterPickerRow
            label="Usuario"
            valueLabel={userPickerLabel}
            active={filters.userFilter != null}
            onPress={() => setUserSheetOpen(true)}
            accessibilityLabel={`Usuario, ${userPickerLabel}`}
          />
        </>
      ) : null}
    </>
  );

  const activeCount = filters.hasActiveFilters ? 1 : 0;

  return (
    <>
      <OperationalFiltersCard>
        <TouchableOpacity
          style={styles.collapseHeader}
          onPress={toggleCollapsed}
          accessibilityRole="button"
          accessibilityLabel={collapsed ? 'Mostrar filtros' : 'Ocultar filtros'}
          accessibilityState={{ expanded: !collapsed }}
        >
          <View style={styles.collapseHeaderMain}>
            <OperationalFilterFieldLabel>Filtros</OperationalFilterFieldLabel>
            {collapsed && activeCount > 0 ? (
              <Pressable
                onPress={(e) => { e.stopPropagation(); toggleCollapsed(); }}
                accessibilityRole="button"
                accessibilityLabel={`${activeCount} filtros activos, expandir panel`}
              >
                <View style={styles.activeChip}>
                  <Text style={styles.activeChipText}>
                    {activeCount} activo{activeCount === 1 ? '' : 's'}
                  </Text>
                </View>
              </Pressable>
            ) : null}
          </View>
          <Ionicons
            name={collapsed ? 'chevron-down' : 'chevron-up'}
            size={18}
            color={ticketColors.ink3}
          />
        </TouchableOpacity>

        {collapsed ? null : (
          <View style={styles.body}>
            {metaRow}
            {metaRow ? <OperationalFilterSectionDivider /> : null}
            {isMobile ? mobileFilters : desktopCompactPickers}
          </View>
        )}
      </OperationalFiltersCard>

      <MasterFilterSheetModal
        visible={periodSheetOpen}
        title="Período"
        options={periodSheetOptions}
        selectedId={filters.periodPreset}
        onSelect={(id) => {
          if (id) filters.setPeriodPreset(id as AuditPeriodPreset);
        }}
        onClose={() => setPeriodSheetOpen(false)}
        showAllOption={false}
      />

      {showUserFilter ? (
        <MasterFilterSheetModal
          visible={userSheetOpen}
          title="Usuario"
          options={userOptions}
          selectedId={filters.userFilter}
          onSelect={(id) => filters.setUserFilter(id)}
          onClose={() => setUserSheetOpen(false)}
          allOptionLabel="Todos"
        />
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  collapseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  collapseHeaderMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 0,
  },
  activeChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
  },
  activeChipText: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '800',
  },
  body: {
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  customDateRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
    alignSelf: 'stretch',
  },
  customDateField: {
    flex: 1,
    minWidth: 0,
    maxWidth: 220,
  },
});
